import React, { useState, useRef, useEffect } from 'react';
import { Form, Input, Button, Typography, Alert, Tabs, Modal, message, Divider } from 'antd';
import { MailOutlined, RobotOutlined, LockOutlined, UserOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { registerUser, getValidationPolicy, resendVerification, getEntraSsoConfig, startEntraLogin } from '../services/authService';
import { WindowsFilled } from '@ant-design/icons';
import './AccessGate.css';

const { Title, Text, Paragraph } = Typography;

function AccessGate() {
  const { login, authError, isLoading } = useAuth();
  const { themeConfig } = useTheme();
  const [activeTab, setActiveTab] = useState('signin');

  // Sign In state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState('');
  const usernameRef = useRef(null);

  // Sign Up state
  const [regEmail, setRegEmail] = useState('');
  const [regUsername, setRegUsername] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirm, setRegConfirm] = useState('');
  const [regFullName, setRegFullName] = useState('');
  const [regLoading, setRegLoading] = useState(false);
  const [regError, setRegError] = useState('');
  const [regSuccess, setRegSuccess] = useState('');
  const [policy, setPolicy] = useState(null);

  // Verification resend
  const [verifyModalVisible, setVerifyModalVisible] = useState(false);
  const [verifyUsername, setVerifyUsername] = useState('');

  // Microsoft Entra SSO availability — controlled by the backend (/auth/entra/config).
  // When false the button is hidden so password login is the only option.
  const [ssoEnabled, setSsoEnabled] = useState(false);

  useEffect(() => {
    if (usernameRef.current) usernameRef.current.focus();
    getValidationPolicy().then(p => p && setPolicy(p)).catch(() => {});
    getEntraSsoConfig().then(cfg => setSsoEnabled(Boolean(cfg && cfg.enabled))).catch(() => {});
  }, []);

  // Surface SSO callback errors (set by AuthContext after the redirect).
  const ssoErrorMatch = typeof authError === 'string' && authError.startsWith('SSO_ERROR:')
    ? authError.slice('SSO_ERROR:'.length) : null;
  const ssoErrorMessage = ssoErrorMatch && ({
    pending_approval: 'Your account is awaiting administrator approval.',
    account_rejected: 'Your access request was declined. Please contact your administrator.',
    account_disabled: 'Your account is disabled. Please contact your administrator.',
    domain_not_allowed: 'Your email domain is not allowed to sign in via Microsoft SSO.',
    no_email_in_token: 'Microsoft did not return an email address for your account.',
  }[ssoErrorMatch] || `Microsoft sign-in failed (${ssoErrorMatch}).`);

  // ── Sign In ───────────────────────────────────────────────
  const handleSignIn = async () => {
    if (isLoading) return;
    if (!username.trim()) { setLocalError('Username is required.'); return; }
    if (!password.trim()) { setLocalError('Password is required.'); return; }
    setLocalError('');
    const result = await login(username.trim(), password);
    if (!result.success) {
      const err = result.error || '';
      if (err === 'EMAIL_NOT_VERIFIED' || err.includes('EMAIL_NOT_VERIFIED')) {
        setVerifyUsername(username.trim());
        setVerifyModalVisible(true);
      }
    }
  };

  // ── Sign Up ───────────────────────────────────────────────
  const handleSignUp = async () => {
    setRegError('');
    setRegSuccess('');
    if (!regEmail.trim() || !regUsername.trim() || !regPassword || !regConfirm) {
      setRegError('All fields are required.'); return;
    }
    if (regPassword !== regConfirm) {
      setRegError('Passwords do not match.'); return;
    }
    if (policy) {
      if (regPassword.length < (policy.password_min_length || 8)) {
        setRegError(`Password must be at least ${policy.password_min_length} characters.`); return;
      }
      if (policy.password_require_uppercase && !/[A-Z]/.test(regPassword)) {
        setRegError('Password must contain an uppercase letter.'); return;
      }
      if (policy.password_require_lowercase && !/[a-z]/.test(regPassword)) {
        setRegError('Password must contain a lowercase letter.'); return;
      }
      if (policy.password_require_digit && !/\d/.test(regPassword)) {
        setRegError('Password must contain a digit.'); return;
      }
      if (policy.password_require_special_char && !/[!@#$%^&*(),.?":{}|<>]/.test(regPassword)) {
        setRegError('Password must contain a special character.'); return;
      }
    }
    setRegLoading(true);
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      await registerUser({
        email: regEmail.trim(),
        username: regUsername.trim(),
        password: regPassword,
        full_name: regFullName.trim() || regUsername.trim(),
        timezone: tz,
      });
      setRegSuccess('Account created! A verification email has been sent. After you verify, your access request will be reviewed by an administrator — you will receive an email once it is approved.');
      setRegEmail(''); setRegUsername(''); setRegPassword(''); setRegConfirm(''); setRegFullName('');
    } catch (err) {
      setRegError(err.message || 'Registration failed.');
    } finally {
      setRegLoading(false);
    }
  };

  // ── Resend Verification ───────────────────────────────────
  const handleResendVerification = async () => {
    try {
      await resendVerification(verifyUsername);
      message.success('Verification email re-sent! Please check your inbox (and spam folder).');
      setVerifyModalVisible(false);
    } catch (err) {
      message.error(err.message);
    }
  };

  const isEmailNotVerified = authError === 'EMAIL_NOT_VERIFIED';
  const isSsoPendingApproval = ssoErrorMatch === 'pending_approval';
  const isPendingApproval = authError === 'PENDING_ADMIN_APPROVAL' || isSsoPendingApproval;
  const isRejected = authError === 'ACCOUNT_REJECTED';
  const isApprovalState = isPendingApproval || isRejected;
  const isSsoError = Boolean(ssoErrorMatch) && !isSsoPendingApproval;
  const displayError = localError
    || (isSsoError ? ssoErrorMessage : '')
    || (authError && !isEmailNotVerified && !isApprovalState && !isSsoError ? authError : '');

  // Rendered above the password form on both tabs when SSO is enabled.
  const ssoBlock = ssoEnabled ? (
    <div style={{ marginBottom: 16 }} data-testid="entra-sso-block">
      <Button
        block
        size="large"
        icon={<WindowsFilled style={{ color: '#0078D4' }} />}
        onClick={() => startEntraLogin(window.location.pathname || '/')}
        data-testid="entra-sso-button"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontWeight: 600 }}
      >
        Sign in with Cox SSO
      </Button>
      <Divider plain style={{ margin: '12px 0', fontSize: 12, color: '#888' }}>or</Divider>
    </div>
  ) : null;

  const iconColor = themeConfig?.primary || '#c9a961';
  const labelColor = themeConfig?.textPrimary || '#1f2937';

  return (
    <div className="access-gate-overlay" data-testid="login-page-container">
      <div className="access-gate-container" data-testid="login-card">
        {/* Logo / Branding */}
        <div className="access-gate-logo" data-testid="login-header">
          <div className="access-gate-icon">
            <RobotOutlined style={{ fontSize: '40px', color: '#ffffff' }} />
          </div>
          <Title level={2} className="access-gate-title">EE Data Assistant</Title>
          <Paragraph className="access-gate-subtitle">Intelligent insights at your fingertips</Paragraph>
        </div>

        {/* Lock indicator */}
        <div className="access-gate-lock-badge">
          <LockOutlined style={{ fontSize: '13px', marginRight: '6px' }} />
          <Text style={{ fontSize: '12px', letterSpacing: '1px', textTransform: 'uppercase', fontWeight: 600, color: 'inherit' }}>
            Secure Access
          </Text>
        </div>

        {/* Tabs: Sign In / Sign Up */}
        <div className="access-gate-form">
          <Tabs activeKey={activeTab} onChange={setActiveTab} centered size="large" data-testid="login-tabs"
            items={[
              {
                key: 'signin',
                label: 'Sign In',
                children: (
                  <>
                    {ssoBlock}
                    {displayError && (
                      <Alert type="error" showIcon message={displayError}
                        style={{ marginBottom: 16, borderRadius: 8 }} closable onClose={() => setLocalError('')} />
                    )}
                    {isEmailNotVerified && (
                      <Alert
                        type="warning"
                        showIcon
                        message="Email Not Verified"
                        description={
                          <span>
                            Your account email has not been verified. Please check your inbox (including spam folder).{' '}
                            <a onClick={() => { setVerifyUsername(username.trim()); setVerifyModalVisible(true); }}
                              style={{ cursor: 'pointer', fontWeight: 600 }}>
                              Resend verification email
                            </a>
                          </span>
                        }
                        style={{ marginBottom: 16, borderRadius: 8 }}
                        closable
                        onClose={() => {}}
                      />
                    )}
                    {isPendingApproval && (
                      <Alert
                        type="success"
                        showIcon
                        message="Awaiting Administrator Approval"
                        description="Your access request has been sent to the administrators. You will receive an email once it is approved."
                        style={{ marginBottom: 16, borderRadius: 8, backgroundColor: '#f6ffed', borderColor: '#b7eb8f', color: '#135200' }}
                      />
                    )}
                    {isRejected && (
                      <Alert
                        type="error"
                        showIcon
                        message="Access Request Declined"
                        description="Your access request was not approved. Please contact your administrator if you believe this was a mistake."
                        style={{ marginBottom: 16, borderRadius: 8 }}
                      />
                    )}
                    <Form layout="vertical" onFinish={handleSignIn} requiredMark={false} data-testid="login-form">
                      <Form.Item label={<span className="access-gate-label" style={{ color: labelColor }}>Email or Username</span>}>
                        <Input ref={usernameRef} prefix={<UserOutlined style={{ color: iconColor }} />}
                          value={username} onChange={e => { setUsername(e.target.value); setLocalError(''); }}
                          onPressEnter={handleSignIn} placeholder="Enter your email or username" size="large"
                          disabled={isLoading} className="access-gate-input" allowClear
                          data-testid="login-username-input" />
                      </Form.Item>
                      <Form.Item label={<span className="access-gate-label" style={{ color: labelColor }}>Password</span>}>
                        <Input.Password prefix={<LockOutlined style={{ color: iconColor }} />}
                          value={password} onChange={e => { setPassword(e.target.value); setLocalError(''); }}
                          onPressEnter={handleSignIn} placeholder="Enter your password" size="large"
                          disabled={isLoading} className="access-gate-input"
                          iconRender={v => v ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
                          data-testid="login-password-input" />
                      </Form.Item>
                      <Button type="primary" htmlType="submit" size="large" block
                        disabled={!username.trim() || !password.trim()} loading={isLoading}
                        className="access-gate-btn" data-testid="login-submit-button">
                        {isLoading ? 'Signing in...' : 'Sign In'}
                      </Button>
                    </Form>
                  </>
                ),
              },
              {
                key: 'signup',
                label: 'Sign Up',
                children: (
                  <>
                    {ssoBlock}
                    {regError && <Alert type="error" showIcon message={regError} style={{ marginBottom: 12, borderRadius: 8 }} closable onClose={() => setRegError('')} />}
                    {regSuccess && <Alert type="success" showIcon message={regSuccess} style={{ marginBottom: 12, borderRadius: 8, backgroundColor: '#f6ffed', borderColor: '#b7eb8f', color: '#135200' }} />}
                    <Form layout="horizontal" onFinish={handleSignUp} requiredMark={false}
                      labelCol={{ span: 9 }} wrapperCol={{ span: 15 }} labelAlign="left"
                      data-testid="register-form">
                      <Form.Item label={<span className="access-gate-label" style={{ color: labelColor }}>Email</span>}>
                        <Input prefix={<MailOutlined style={{ color: iconColor }} />}
                          value={regEmail} onChange={e => setRegEmail(e.target.value)}
                          placeholder="you@company.com" size="large" className="access-gate-input"
                          data-testid="register-email-input" />
                      </Form.Item>
                      <Form.Item label={<span className="access-gate-label" style={{ color: labelColor }}>Username</span>}>
                        <Input prefix={<UserOutlined style={{ color: iconColor }} />}
                          value={regUsername} onChange={e => setRegUsername(e.target.value)}
                          placeholder="Choose a username" size="large" className="access-gate-input"
                          data-testid="register-username-input" />
                      </Form.Item>
                      <Form.Item label={<span className="access-gate-label" style={{ color: labelColor }}>Full Name</span>}>
                        <Input prefix={<UserOutlined style={{ color: iconColor }} />}
                          value={regFullName} onChange={e => setRegFullName(e.target.value)}
                          placeholder="Your full name (optional)" size="large" className="access-gate-input" />
                      </Form.Item>
                      <Form.Item label={<span className="access-gate-label" style={{ color: labelColor }}>Password</span>}>
                        <Input.Password prefix={<LockOutlined style={{ color: iconColor }} />}
                          value={regPassword} onChange={e => setRegPassword(e.target.value)}
                          placeholder="Create a password" size="large" className="access-gate-input"
                          iconRender={v => v ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
                          data-testid="register-password-input" />
                      </Form.Item>
                      <Form.Item label={<span className="access-gate-label" style={{ color: labelColor }}>Confirm Password</span>}>
                        <Input.Password prefix={<LockOutlined style={{ color: iconColor }} />}
                          value={regConfirm} onChange={e => setRegConfirm(e.target.value)}
                          placeholder="Confirm your password" size="large" className="access-gate-input"
                          iconRender={v => v ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
                          data-testid="register-confirm-password-input" />
                      </Form.Item>
                      {policy && (
                        <div style={{ fontSize: 12, color: themeConfig?.textSecondary || '#4b5563', marginBottom: 12 }}>
                          Min {policy.password_min_length} chars
                          {policy.password_require_uppercase && ', uppercase'}
                          {policy.password_require_lowercase && ', lowercase'}
                          {policy.password_require_digit && ', digit'}
                          {policy.password_require_special_char && ', special char'}
                        </div>
                      )}
                      <Button type="primary" htmlType="submit" size="large" block
                        loading={regLoading} className="access-gate-btn" data-testid="register-submit-button">
                        {regLoading ? 'Creating Account...' : 'Create Account'}
                      </Button>
                    </Form>
                  </>
                ),
              },
            ]}
          />
        </div>

        <Divider style={{ margin: '8px 0 0 0' }} />
        <div className="access-gate-footer">
          <Text className="access-gate-footer-text">
            {activeTab === 'signin'
              ? "Don't have an account? Switch to Sign Up"
              : 'A verification email will be sent to your inbox'}
          </Text>
        </div>
      </div>

      {/* Email Verification Modal */}
      <Modal title="Email Not Verified" open={verifyModalVisible}
        zIndex={10000}
        onCancel={() => setVerifyModalVisible(false)}
        onOk={handleResendVerification} okText="Resend Verification Email">
        <p>Your email has not been verified yet. Please complete the email verification step to continue.</p>
        <p>Click <strong>Resend Verification Email</strong> to receive a new verification link.</p>
      </Modal>
    </div>
  );
}

export default AccessGate;
